import { Box } from '@material-ui/core';
import { formatMoney, getMoneySymbolByCurrency, PaymentPlan, PaymentPlanLine } from '@nemuru-eng/js-api-client';

import { PaymentStatusOptions } from '@/components/modules/PaymentPlan/components/PaymentPlanTableSection/components/PaymentPlanTable/components/PaymentPlanLineActionsDialog/constants/PaymentLineStatusOptions';
import { usePaymentPlanLine } from '@/components/modules/PaymentPlan/components/PaymentPlanTableSection/components/PaymentPlanTable/components/PaymentPlanLineActionsDialog/hooks/usePaymentPlanLine';
import {
  UpdateDueDateSchema,
  UpdateDueDateType,
} from '@/components/modules/PaymentPlan/components/PaymentPlanTableSection/components/PaymentPlanTable/components/PaymentPlanLineActionsDialog/schemas/UpdateDueDateSchema';
import {
  UpdateMonthlyQuotaSchema,
  UpdateMonthlyQuotaType,
} from '@/components/modules/PaymentPlan/components/PaymentPlanTableSection/components/PaymentPlanTable/components/PaymentPlanLineActionsDialog/schemas/UpdateMonthlyQuotaSchema';
import {
  UpdatePaymentDateSchema,
  UpdatePaymentDateType,
} from '@/components/modules/PaymentPlan/components/PaymentPlanTableSection/components/PaymentPlanTable/components/PaymentPlanLineActionsDialog/schemas/UpdatePaymentDateSchema';
import {
  UpdatePaymentStatusSchema,
  UpdatePaymentStatusType,
} from '@/components/modules/PaymentPlan/components/PaymentPlanTableSection/components/PaymentPlanTable/components/PaymentPlanLineActionsDialog/schemas/UpdatePaymentStatusSchema';
import { PaymentPlanLineTableAction } from '@/components/modules/PaymentPlan/components/PaymentPlanTableSection/components/PaymentPlanTable/types/PaymentPlanLineTableAction';
import { DateFormField } from '@/components/shared/components/NewContextForms/DateFormField';
import { NumberFormField } from '@/components/shared/components/NewContextForms/NumberFormField';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';

interface PaymentPlanLineActionsDialogProps {
  changePaymentAction: PaymentPlanLineTableAction;
  opened: boolean;
  setOpened: (boolean) => void;
  toggleDialog: () => void;
  paymentPlanLine: PaymentPlanLine;
  paymentPlan: PaymentPlan;
}

export const PaymentPlanLineActionsDialog = ({
  changePaymentAction,
  opened,
  setOpened,
  toggleDialog,
  paymentPlanLine,
  paymentPlan,
}: PaymentPlanLineActionsDialogProps) => {
  const {
    updateDueDateByPaymentLineId,
    updatePaymentDateByPaymentLineId,
    updatePaymentPlanLineQuota,
    updatePaymentPlanLineStatus,
    loading,
  } = usePaymentPlanLine(paymentPlan, paymentPlanLine);
  const { getTextIdTranslation } = useTranslation();
  const getChangePaymentActionParams = () => {
    switch (changePaymentAction) {
      case PaymentPlanLineTableAction.UPDATE_PAYMENT_PLAN_LINE_DUE_DATE:
        return {
          children: (
            <Box pt={4}>
              <DateFormField name={'dueDate'} label={'DUE_DATE'} value={paymentPlanLine.dueDate} disablePast />
            </Box>
          ),
          onSubmitAction: async (values: UpdateDueDateType) => {
            await updateDueDateByPaymentLineId(values);
            toggleDialog();
          },
          defaultValues: { dueDate: paymentPlanLine.dueDate },
          schema: () => UpdateDueDateSchema(getTextIdTranslation),
        };
      case PaymentPlanLineTableAction.UPDATE_PAYMENT_PLAN_LINE_PAYMENT_DATE:
        return {
          children: (
            <Box pt={4}>
              <DateFormField
                name={'paymentDate'}
                label={'PAYMENT_DATE'}
                value={paymentPlanLine.paymentDate}
                disableFuture
              />
            </Box>
          ),
          onSubmitAction: async (values: UpdatePaymentDateType) => {
            await updatePaymentDateByPaymentLineId(values);
            toggleDialog();
          },
          defaultValues: {},
          schema: () => UpdatePaymentDateSchema(getTextIdTranslation),
        };
      case PaymentPlanLineTableAction.UPDATE_PAYMENT_PLAN_LINE_QUOTA_AMOUNT:
        return {
          children: (
            <Box pt={4}>
              <NumberFormField
                name={'amount'}
                label={'MONTHLY_QUOTA'}
                value={formatMoney(
                  { value: paymentPlanLine.quota.amount, currency: paymentPlanLine.quota.currency },
                  2,
                )}
                valueUnit={getMoneySymbolByCurrency(paymentPlanLine.quota.currency)}
              />
            </Box>
          ),
          onSubmitAction: async (values: UpdateMonthlyQuotaType) => {
            await updatePaymentPlanLineQuota(values);
            toggleDialog();
          },
          defaultValues: {},
          schema: UpdateMonthlyQuotaSchema,
        };
      case PaymentPlanLineTableAction.UPDATE_PAYMENT_PLAN_LINE_STATUS:
        return {
          children: (
            <Box pt={4}>
              <SelectFormField
                name={'status'}
                value={paymentPlanLine.status}
                label={'PAYMENT_LINE_STATUS'}
                selectOptions={PaymentStatusOptions}
              />
            </Box>
          ),
          onSubmitAction: async (values: UpdatePaymentStatusType) => {
            await updatePaymentPlanLineStatus(values);
            toggleDialog();
          },
          defaultValues: {},
          schema: UpdatePaymentStatusSchema,
        };
      default:
        return undefined;
    }
  };

  const changePaymentActionParams = getChangePaymentActionParams();

  if (!changePaymentActionParams) return <></>;

  const formSchema = changePaymentActionParams.schema;
  const defaultValues = changePaymentActionParams.defaultValues;

  return (
    <EditDialogWrapper
      title={changePaymentAction}
      onSubmitForm={changePaymentActionParams.onSubmitAction}
      opened={opened}
      setOpened={setOpened}
      handleClose={toggleDialog}
      isLoading={loading}
      saveButtonTextId={`${changePaymentAction}_BTN`}
      formSchema={formSchema}
      defaultValues={defaultValues}
    >
      <>
        <Font variant={'body2'} color={'textSecondary'}>
          <Text tId={`${changePaymentAction}_TEXT`} />
        </Font>
        {changePaymentActionParams.children}
      </>
    </EditDialogWrapper>
  );
};
